import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import { AppButton, Spinning, SimpleModal } from '@Components/Control';
import { ProductFormSingleTextInput, ProductFormDropdownSelect } from '@Components/Partial/Product/NewSearch';
import { buildDateTimeLabel, getMatchPolicyAction } from '@Helpers/util';
import { paths } from '@Components/configs';
import { NoDataPlaceholder } from '../../SharedComponents';
import { StyledRaisedContainer } from '../../StyledSharedComponents';
import { StyledStatusBadge } from '../../ListSearches/StyledListSearches';
import { MATCH_POLICY_ACTIONS } from '../constants';

const AssetDetails = ({
  small,
  asset,
  loading,
  formValues,
  setFormValues,
  onSubmit,
  submitEnabled,
  matchPolicy,
  matchPolicyLoading,
  matchPolicyFormValue,
  setMatchPolicyFormValue,
  onMatchPolicySubmit,
  matchPolicySubmitEnabled,
  onOpenReleaseModal,
  onReleaseAllClaims,
  releaseClaimsCount,
  releaseCountLoading,
  releaseAllSubmitting,
  activeReleaseRequest,
  onRevertReleaseRequest,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const [showReleaseModal, setShowReleaseModal] = React.useState(false);

  const isPendingRelease = activeReleaseRequest?.requestStatus === 'PENDING';
  const isUnderReview = activeReleaseRequest?.requestStatus === 'UNDER_REVIEW';
  const hasActiveRelease = isPendingRelease || isUnderReview;
  const isRevertMode = isPendingRelease && showReleaseModal;
  const confirmLabel = isRevertMode ? 'Confirm revert' : 'Confirm release';

  const handleOpenReleaseModal = () => {
    setShowReleaseModal(true);
    if (!hasActiveRelease && onOpenReleaseModal) onOpenReleaseModal();
  };

  const handleConfirmRelease = () => {
    if (isRevertMode) {
      if (onRevertReleaseRequest) {
        onRevertReleaseRequest(() => setShowReleaseModal(false));
      }
    } else if (onReleaseAllClaims) {
      onReleaseAllClaims(() => setShowReleaseModal(false));
    }
  };

  if (loading) {
    return (
      <Box flex align="center" justify="center">
        <Spinning size="large" color={page.highlight} />
      </Box>
    );
  }

  if (!loading && !asset) {
    return (
      <NoDataPlaceholder
        loading={false}
        label="Asset could not be found."
        textColor={page.primaryText}
        loaderColor={page.highlight}
      />
    );
  }

  const handleFormValueChange = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const labelString = Array.isArray(asset.label) ? asset.label.join(', ') : asset.label || '';
  const formattedTime = asset.timeCreated ? buildDateTimeLabel(asset.timeCreated) : '';
  const currentAction = getMatchPolicyAction(matchPolicy);
  const activeAction = matchPolicyFormValue || currentAction;
  const actionEntry = MATCH_POLICY_ACTIONS.find((a) => a.api === activeAction);
  const displayAction = (actionEntry ? actionEntry.display : activeAction).toUpperCase();

  return (
    <Box
      direction={small ? 'column' : 'row'}
      gap="2rem"
      width="100%"
      align="start"
    >
      {/* Left Column: Asset Info + Match Policy */}
      <Box
        direction="column"
        gap="1rem"
        width={small ? '100%' : '50%'}
        align="start"
      >
        {/* Asset Info Card */}
        <StyledRaisedContainer
          direction="column"
          gap="0.75rem"
          pad={{ horizontal: '1.5rem', vertical: '1rem' }}
          borderColor={page.tableBorder}
          margin={{ top: '0' }}
          width="100%"
        >
          <Text size="1.25rem" weight={600} color={page.primaryText}>
            Asset Info
          </Text>
          <Box direction="column" gap="1.5rem">
            <Box direction="row" gap="0.5rem" align="center">
              <Text weight={500} color={page.primaryText}>Asset label:</Text>
              <StyledStatusBadge background="#EDFDF5">
                <Text size="1rem" color="#22684B" weight={600}>
                  {labelString}
                </Text>
              </StyledStatusBadge>
            </Box>
            <Box direction="row" gap="0.5rem" align="center">
              <Text weight={500} color={page.primaryText}>Unique ID:</Text>
              <Text color={page.primaryText}>{asset.id}</Text>
            </Box>
            <Box direction="row" gap="0.5rem" align="center">
              <Text weight={500} color={page.primaryText}>Created at:</Text>
              <Text color={page.primaryText}>{formattedTime}</Text>
            </Box>
            <Box direction="row" gap="0.75rem" align="center">
              <AppButton
                overrideHover
                width="7rem"
                href={paths.productClaimsList.replace(':assetId', asset.id)}
                level="dynamicLarge"
                color={button.primaryText}
                bgColor={button.primaryBg}
                fontWeight={600}
                label="View claims"
              />
              {isUnderReview ? (
                <AppButton
                  overrideHover
                  width="10rem"
                  level="dynamicLarge"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Under review"
                  disabled
                />
              ) : (
                <AppButton
                  overrideHover
                  width="10rem"
                  onClick={handleOpenReleaseModal}
                  level="dynamicLarge"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label={isPendingRelease ? 'Revert release' : 'Release all claims'}
                />
              )}
            </Box>
            {showReleaseModal && (
              <SimpleModal
                small={small}
                toggleModal={setShowReleaseModal}
                modalTitle={isRevertMode ? 'Revert release request' : 'Release all claims'}
                hideLowerSection
                noClickOutside
                desktopMinWidth="36rem"
              >
                <Box direction="column" gap="1.25rem" pad={{ vertical: '1.25rem' }}>
                  <Text color={page.primaryText}>
                    {isRevertMode
                      ? 'Are you sure you want to revert the pending release request for this asset? The claims will be restored to their previous status.'
                      : 'Are you sure you want to submit a release request for all claims on this asset? This cannot be undone.'}
                  </Text>
                  <Box
                    direction="column"
                    gap="0.6rem"
                    pad="1rem"
                    round="8px"
                    background={page.altBg || page.background}
                    border={{ color: page.tableBorder, size: '1px' }}
                  >
                    <Box direction="row" gap="0.5rem">
                      <Text weight={600} color={page.primaryText} style={{ minWidth: '5rem' }}>Asset ID:</Text>
                      <Text color={page.primaryText}>{asset.id}</Text>
                    </Box>
                    <Box direction="row" gap="0.5rem">
                      <Text weight={600} color={page.primaryText} style={{ minWidth: '5rem' }}>Title:</Text>
                      <Text color={page.primaryText}>{asset.metadata?.title || '—'}</Text>
                    </Box>
                    <Box direction="row" gap="0.5rem" align="center">
                      <Text weight={600} color={page.primaryText} style={{ minWidth: '5rem' }}>Claims:</Text>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isRevertMode ? (
                        <Text color={page.primaryText}>
                          {activeReleaseRequest?.items?.length ?? '—'}
                        </Text>
                      ) : releaseCountLoading ? (
                        <Spinning size="small" color={page.highlight} />
                      ) : (
                        <Text color={page.primaryText}>
                          {releaseClaimsCount !== null && releaseClaimsCount !== undefined
                            ? releaseClaimsCount
                            : '—'}
                        </Text>
                      )}
                    </Box>
                  </Box>
                  <Box direction="row" gap="0.75rem" justify="end" margin={{ top: '0.5rem' }}>
                    <AppButton
                      overrideHover
                      label="Cancel"
                      onClick={() => setShowReleaseModal(false)}
                      level="dynamicSmall"
                      color={page.primaryText}
                      bgColor={page.tableBorder}
                      fontWeight={600}
                    />
                    <AppButton
                      overrideHover
                      label={releaseAllSubmitting ? 'Submitting...' : confirmLabel}
                      disabled={releaseAllSubmitting || (!isRevertMode && releaseCountLoading)}
                      onClick={handleConfirmRelease}
                      level="dynamicSmall"
                      width="9rem"
                      color={button.primaryText}
                      bgColor={button.primaryBg}
                      fontWeight={600}
                    />
                  </Box>
                </Box>
              </SimpleModal>
            )}
          </Box>
        </StyledRaisedContainer>
        {/* Match Policy Card */}
        <StyledRaisedContainer
          direction="column"
          gap="1.25rem"
          pad={{ horizontal: '1.5rem', vertical: '1rem' }}
          borderColor={page.tableBorder}
          margin={{ top: '0' }}
          width="100%"
        >
          <Box direction="column" gap="0.5rem">
            <Text size="1.25rem" weight={600} color={page.primaryText}>
              Asset Match Policy
            </Text>
            <Text size="0.85rem" color={page.hintText}>
              The policy you select here tells YouTube which
              action to take when a match is found for this asset
            </Text>
          </Box>
          {matchPolicyLoading ? (
            <Box align="center" justify="center" pad="2rem">
              <Spinning size="medium" color={page.highlight} />
            </Box>
          ) : (
            <Box direction="column" gap="1.5rem">
              <Box direction="column" gap="0.5rem">
                <Text weight={500} color={page.primaryText}>
                  Current policy:
                  {' '}
                  {displayAction}
                </Text>
                <Box width={small ? '100%' : '50%'}>
                  <ProductFormDropdownSelect
                    overrideOptions={MATCH_POLICY_ACTIONS}
                    selectable
                    keepLabel
                    value={Case.capital(matchPolicyFormValue || currentAction)}
                    handleFormValues={setMatchPolicyFormValue}
                  />
                </Box>
              </Box>
              <Box direction="row" gap="1rem" margin={{ top: '1.5rem' }}>
                <AppButton
                  overrideHover
                  width="7rem"
                  disabled={!matchPolicySubmitEnabled}
                  onClick={onMatchPolicySubmit}
                  level="dynamicLarge"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Save"
                />
              </Box>
            </Box>
          )}
        </StyledRaisedContainer>
      </Box>
      {/* Right Column: Metadata */}
      <StyledRaisedContainer
        direction="column"
        gap="1.25rem"
        pad={{ horizontal: '1.5rem', vertical: '1rem' }}
        borderColor={page.tableBorder}
        margin={{ top: '0' }}
        width={small ? '100%' : '50%'}
      >
        <Box direction="column" gap="0.5rem">
          <Text size="1.25rem" weight={600} color={page.primaryText}>
            Metadata
          </Text>
          <Text size="0.85rem" color={page.hintText}>
            These values can be edited and saved to help you
            better organize and identify your assets
          </Text>
        </Box>
        <Box direction="column" gap="1.5rem">
          <Box direction="column" gap="0.5rem">
            <Text weight={500} color={page.primaryText}>Title</Text>
            <ProductFormSingleTextInput
              focusHighlight={page.focusHighlight}
              value={formValues.title}
              handleFormValues={(value) => handleFormValueChange('title', value)}
              placeholder="Title"
            />
          </Box>
          <Box direction="column" gap="0.5rem">
            <Text weight={500} color={page.primaryText}>Description</Text>
            <ProductFormSingleTextInput
              focusHighlight={page.focusHighlight}
              value={formValues.description}
              handleFormValues={(value) => handleFormValueChange('description', value)}
              placeholder="Description"
            />
          </Box>
          <Box direction="column" gap="0.5rem">
            <Text weight={500} color={page.primaryText}>Notes</Text>
            <ProductFormSingleTextInput
              focusHighlight={page.focusHighlight}
              value={formValues.notes}
              handleFormValues={(value) => handleFormValueChange('notes', value)}
              placeholder="Notes"
            />
          </Box>
          <Box direction="column" gap="0.5rem">
            <Text weight={500} color={page.primaryText}>Custom ID</Text>
            <ProductFormSingleTextInput
              focusHighlight={page.focusHighlight}
              value={formValues.customId}
              handleFormValues={(value) => handleFormValueChange('customId', value)}
              placeholder="Custom ID"
            />
          </Box>
        </Box>
        <Box direction="row" gap="1rem" margin={{ top: '1.5rem' }}>
          <AppButton
            overrideHover
            width="7rem"
            disabled={!submitEnabled}
            onClick={onSubmit}
            level="dynamicLarge"
            color={button.primaryText}
            bgColor={button.primaryBg}
            fontWeight={600}
            label="Save"
          />
        </Box>
      </StyledRaisedContainer>
    </Box>
  );
};

AssetDetails.propTypes = {
  small: PropTypes.bool.isRequired,
  asset: PropTypes.object,
  loading: PropTypes.bool,
  formValues: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    notes: PropTypes.string,
    customId: PropTypes.string,
  }).isRequired,
  setFormValues: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitEnabled: PropTypes.bool.isRequired,
  matchPolicy: PropTypes.shape({
    kind: PropTypes.string,
    policyId: PropTypes.string,
    rules: PropTypes.arrayOf(PropTypes.shape({
      action: PropTypes.string,
      subaction: PropTypes.array,
      conditions: PropTypes.object,
      appliesGlobally: PropTypes.bool,
    })),
  }),
  matchPolicyLoading: PropTypes.bool,
  matchPolicyFormValue: PropTypes.string,
  setMatchPolicyFormValue: PropTypes.func.isRequired,
  onMatchPolicySubmit: PropTypes.func.isRequired,
  matchPolicySubmitEnabled: PropTypes.bool.isRequired,
  onOpenReleaseModal: PropTypes.func,
  onReleaseAllClaims: PropTypes.func,
  releaseClaimsCount: PropTypes.number,
  releaseCountLoading: PropTypes.bool,
  releaseAllSubmitting: PropTypes.bool,
  activeReleaseRequest: PropTypes.object,
  onRevertReleaseRequest: PropTypes.func,
};

export default AssetDetails;
